import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase, isConfigured } from '../lib/supabase.js'
import { useToast } from '../context/ToastContext.jsx'
import Seo from '../components/Seo.jsx'

/*
 * Admin → Integrations. Configures an outbound webhook (e.g. GoHighLevel) that the
 * welcome cron POSTs each new signup to. Settings are stored in private_settings
 * (admin-only RLS) so the webhook URL isn't world-readable. See:
 *   supabase/integrations.sql   and   scripts/send-welcome.mjs (postGhl)
 */
const BLANK = { enabled: false, webhook_url: '', opt_in_only: true }

export default function AdminIntegrations() {
  const toast = useToast()
  const [form, setForm] = useState(BLANK)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    if (!isConfigured) { setLoading(false); return }
    supabase.from('private_settings').select('value').eq('key', 'ghl').maybeSingle().then(({ data, error }) => {
      if (error) setErr(error.message)
      else if (data?.value) setForm({ ...BLANK, ...data.value })
      setLoading(false)
    })
  }, [])

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const save = async () => {
    if (form.enabled && !form.webhook_url.trim()) return toast('Add the webhook URL first', 'red')
    setSaving(true)
    const { error } = await supabase.from('private_settings').upsert({
      key: 'ghl',
      value: { ...form, webhook_url: form.webhook_url.trim() },
      updated_at: new Date().toISOString(),
    })
    setSaving(false)
    if (error) return toast(/row-level|not authorized|permission/i.test(error.message) ? 'Admins only.' : error.message, 'red')
    toast('Integration saved', 'green')
  }

  // Fire a sample contact at the webhook. no-cors: the browser sends it but can't
  // read GoHighLevel's response, so we can only confirm it was dispatched.
  const sendTest = async () => {
    if (!form.webhook_url.trim()) return toast('Add the webhook URL first', 'red')
    setTesting(true)
    try {
      await fetch(form.webhook_url.trim(), {
        method: 'POST',
        mode: 'no-cors',
        body: JSON.stringify({
          email: 'test@calculatemypeptides.com', name: 'Test Contact', first_name: 'Test',
          phone: '', marketing_opt_in: true, source: 'admin-test', device: 'desktop',
          signed_up_at: new Date().toISOString(),
        }),
      })
      toast('Test contact sent — check GoHighLevel to confirm it arrived', 'green')
    } catch (e) {
      toast(`Test failed: ${e.message}`, 'red')
    }
    setTesting(false)
  }

  return (
    <main className="page">
      <Seo title="Integrations | CalculateMyPeptides" noindex />
      <div className="container page-narrow">
        <Link to="/admin" className="pep-back">← Admin</Link>
        <span className="pill blue" style={{ marginBottom: 8 }}>Admin</span>
        <h1 className="section-title">Integrations</h1>
        <p className="section-sub" style={{ maxWidth: '62ch' }}>
          Forward each new signup to an outbound webhook — e.g. a GoHighLevel inbound webhook that creates a contact
          and kicks off your automations.
        </p>

        {loading && <div className="card empty"><div className="spinner" /><p style={{ marginTop: 12 }}>Loading…</p></div>}

        {!loading && !isConfigured && <div className="config-notice"><strong>Backend not connected.</strong></div>}

        {!loading && err && (
          <div className="config-notice">
            <strong>Couldn’t load settings.</strong>
            <p style={{ marginBottom: 0 }}>
              {/relation|does not exist|not authorized|permission/i.test(err)
                ? <>Run <code>supabase/integrations.sql</code> in Supabase (and sign in as an admin), then reload.</>
                : err}
            </p>
          </div>
        )}

        {!loading && !err && isConfigured && (
          <div className="card card-pad">
            <h2 className="pep-h2" style={{ marginTop: 0 }}>GoHighLevel (or any webhook)</h2>

            <label className="consent-row" style={{ marginBottom: 16 }}>
              <input type="checkbox" checked={form.enabled} onChange={set('enabled')} />
              <span><strong>Send new signups to this webhook</strong> <span className="muted">— fires within ~1 minute of signup.</span></span>
            </label>

            <div className="field">
              <label className="label">Webhook URL</label>
              <input className="input" value={form.webhook_url} onChange={set('webhook_url')}
                placeholder="https://services.leadconnectorhq.com/hooks/…" />
              <p className="field-help">
                In GoHighLevel: <strong>Automation → Workflows → + Add New → Inbound Webhook</strong> trigger → copy its
                URL and paste it here. Map the fields below onto your contact in the workflow.
              </p>
            </div>

            <label className="consent-row" style={{ margin: '4px 0 18px' }}>
              <input type="checkbox" checked={form.opt_in_only} onChange={set('opt_in_only')} />
              <span>Only send contacts who <strong>opted into marketing</strong> <span className="muted">(recommended — respects consent).</span></span>
            </label>

            <div className="config-notice" style={{ marginBottom: 18 }}>
              <strong>Fields sent per contact:</strong>
              <p style={{ margin: '4px 0 0' }}>
                <code>email</code>, <code>name</code>, <code>first_name</code>, <code>marketing_opt_in</code> (true/false),
                <code>source</code>, <code>device</code>, <code>signed_up_at</code>, and <code>phone</code>
                <span className="muted"> (empty — phone isn’t collected at signup yet; ask me to add a phone field if you want it).</span>
              </p>
            </div>

            <div className="row" style={{ gap: 10 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-soft" onClick={sendTest} disabled={testing}>{testing ? 'Sending…' : 'Send test contact'}</button>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}
